import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UsersThree, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Section, Field, ErrorBox, Empty, RevisionSelect, SourceTag, VoidForm, inputCls, errMsg, fmtDate } from "./shared";

const TYPES = {
  hedonic: "Hedonic (1–9 liking)",
  triangle: "Triangle (difference test)",
  descriptive: "Descriptive (attribute intensity)",
};

function NewStudyForm({ recipeId, revisions, defaultRev, onDone, onCancel }) {
  const [f, setF] = useState({ revision_id: defaultRev, title: "", type: "hedonic", attributes: "Overall liking, Flavor, Texture" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const attributes = f.type === "triangle" ? [] : f.attributes.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post(`/rd/recipes/${recipeId}/studies`, { revision_id: f.revision_id, title: f.title, type: f.type, attributes, scale_min: 1, scale_max: 9 });
      toast.success("Study created"); onDone();
    } catch (err) { setError(`${errMsg(err)} Your entry is preserved.`); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-md border border-stone-200 bg-stone-50/60 p-4 mb-4 space-y-3" data-testid="form-study">
      <ErrorBox message={error} />
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Revision evaluated"><RevisionSelect revisions={revisions} value={f.revision_id} onChange={(v) => setF({ ...f, revision_id: v })} testId="select-study-revision" /></Field>
        <Field label="Title"><input required className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="input-study-title" /></Field>
        <Field label="Study type">
          <select className={inputCls} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="select-study-type">
            {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        {f.type !== "triangle" && (
          <Field label="Attributes (comma separated)" hint="Scored on a 1–9 scale."><input required className={inputCls} value={f.attributes} onChange={(e) => setF({ ...f, attributes: e.target.value })} data-testid="input-study-attributes" /></Field>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="button-submit-study">{saving ? "Creating…" : "Create study"}</Button>
      </div>
    </form>
  );
}

function ResponseForm({ recipeId, study, onDone }) {
  const attrs = study.attributes || [];
  const [code, setCode] = useState("");
  const [scores, setScores] = useState({});
  const [correct, setCorrect] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isTri = study.type === "triangle";
  const valid = code.trim() && (isTri ? correct !== "" : attrs.every((a) => scores[a] !== undefined && scores[a] !== ""));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      await api.post(`/rd/recipes/${recipeId}/studies/${study.id}/responses`, {
        panelist_code: code.trim(),
        scores: isTri ? {} : Object.fromEntries(attrs.map((a) => [a, Number(scores[a])])),
        correct: isTri ? correct === "yes" : null,
        notes,
      });
      toast.success("Response recorded");
      setCode(""); setScores({}); setCorrect(""); setNotes(""); onDone();
    } catch (err) { setError(`${errMsg(err)} Entry preserved.`); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 rounded-md border border-dashed border-stone-300 p-3 space-y-2" data-testid={`form-response-${study.id}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500">Manual entry from internal panel</div>
      <ErrorBox message={error} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Field label="Panelist code" hint="Anonymous, e.g. P-014"><input className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} data-testid="input-panelist-code" /></Field>
        {isTri ? (
          <Field label="Identified odd sample?">
            <select className={inputCls} value={correct} onChange={(e) => setCorrect(e.target.value)} data-testid="select-triangle-correct">
              <option value="">Select…</option><option value="yes">Correct</option><option value="no">Incorrect</option>
            </select>
          </Field>
        ) : attrs.map((a) => (
          <Field key={a} label={`${a} (${study.scale_min ?? 1}–${study.scale_max ?? 9})`}>
            <input type="number" min={study.scale_min ?? 1} max={study.scale_max ?? 9} step="1" className={inputCls} value={scores[a] ?? ""} onChange={(e) => setScores({ ...scores, [a]: e.target.value })} data-testid={`input-score-${a}`} />
          </Field>
        ))}
      </div>
      <input className={inputCls} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="input-response-notes" />
      <div className="flex justify-end"><Button size="sm" type="submit" disabled={!valid || saving} data-testid="button-submit-response">{saving ? "Saving…" : "Add response"}</Button></div>
    </form>
  );
}

function Summary({ study }) {
  const s = study.summary || {};
  const n = s.count || 0;
  if (n === 0) return <p className="text-sm text-stone-500">No active responses yet — summary appears after the first entry.</p>;
  if (study.type === "triangle") {
    return (
      <div className="text-sm text-stone-800" data-testid={`summary-${study.id}`}>
        <span className="font-mono font-bold">{s.correct ?? 0} / {n}</span> correct identifications.{" "}
        Exact one-sided binomial p (chance = 1/3): <span className="font-mono font-bold">{s.p_value == null ? "n/a" : Number(s.p_value).toPrecision(3)}</span>.
        <div className="text-xs text-stone-500 mt-1">The p-value describes this panel only. It is not a validation of product quality or consumer preference.</div>
      </div>
    );
  }
  const means = Object.entries(s.means || {});
  const max = study.scale_max ?? 9;
  return (
    <div data-testid={`summary-${study.id}`}>
      <div className="text-xs text-stone-500 mb-1.5">Mean scores, n = {n}</div>
      <div className="space-y-1">
        {means.map(([a, m]) => (
          <div key={a} className="grid grid-cols-[120px_1fr_48px] items-center gap-2 text-sm">
            <span className="truncate text-stone-700">{a}</span>
            <div className="h-2 rounded bg-stone-100 overflow-hidden"><div className="h-full bg-[#17324D]/70" style={{ width: `${(Number(m) / max) * 100}%` }} /></div>
            <span className="font-mono text-right">{Number(m).toFixed(2)}</span>
          </div>
        ))}
      </div>
      <div className="text-xs text-stone-500 mt-1">Descriptive means from an internal panel; small n limits interpretation.</div>
    </div>
  );
}

export default function StudiesPanel({ recipeId, recipe, revisions, studies, onReload }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [voiding, setVoiding] = useState(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const noRev = revisions.length === 0;
  const list = studies.filter((s) => !filter || s.revision_id === filter);

  const doVoid = async (study, r, reason) => {
    setVoidBusy(true);
    try {
      await api.post(`/rd/recipes/${recipeId}/studies/${study.id}/responses/${r.id}/void`, { reason });
      toast.success("Response voided"); setVoiding(null); onReload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setVoidBusy(false); }
  };

  return (
    <Section icon={UsersThree} title="Sensory results" testId="rd-studies"
      subtitle="Internal panels only. Responses are entered manually under anonymous panelist codes."
      actions={<Button size="sm" disabled={noRev} onClick={() => setOpen((o) => !o)} className="bg-[#17324D] hover:bg-[#22476B] text-white" data-testid="button-toggle-study-form"><Plus size={13} className="mr-1" /> New study</Button>}>
      {noRev && <Empty>Save a formula revision first — each study evaluates one specific revision.</Empty>}
      {open && <NewStudyForm recipeId={recipeId} revisions={revisions} defaultRev={recipe.current_revision_id || revisions[0]?.id || ""} onCancel={() => setOpen(false)} onDone={() => { setOpen(false); onReload(); }} />}
      {!noRev && <div className="w-full sm:w-80 mb-3"><RevisionSelect allowAll revisions={revisions} value={filter} onChange={setFilter} testId="select-study-filter" /></div>}
      {!noRev && list.length === 0 && <Empty>No sensory studies{filter ? " for this revision" : ""} yet.</Empty>}
      <div className="space-y-4">
        {list.map((s) => (
          <div key={s.id} className="rounded-md border border-stone-200 p-4" data-testid={`card-study-${s.id}`}>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="font-semibold text-stone-800">{s.title}</span>
              <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-800">{s.type}</span>
              <SourceTag revisions={revisions} revisionId={s.revision_id} currentId={recipe.current_revision_id} />
              <span className="text-xs text-stone-400">{fmtDate(s.created_at)}</span>
            </div>
            <Summary study={s} />
            {(s.responses || []).length > 0 && (
              <details className="mt-3">
                <summary className="text-xs text-stone-600 cursor-pointer">Individual responses ({s.responses.length})</summary>
                <div className="mt-2 space-y-1">
                  {s.responses.map((r) => (
                    <div key={r.id} className={`text-xs rounded px-2 py-1.5 ${r.voided ? "bg-stone-50 text-stone-400" : "bg-white border border-stone-100"}`} data-testid={`row-response-${r.id}`}>
                      <div className="flex justify-between gap-2 flex-wrap">
                        <span className={r.voided ? "line-through" : ""}>
                          <span className="font-mono font-bold mr-2">{r.panelist_code}</span>
                          {s.type === "triangle" ? (r.correct ? "correct" : "incorrect") : Object.entries(r.scores || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                          {r.notes && <span className="text-stone-500"> — {r.notes}</span>}
                        </span>
                        {!r.voided && voiding !== r.id && <button className="text-stone-500 hover:text-red-700 underline" onClick={() => setVoiding(r.id)} data-testid={`button-void-response-${r.id}`}>Void</button>}
                      </div>
                      {r.voided && <div className="text-red-700">Voided: {r.void_reason}</div>}
                      {voiding === r.id && <VoidForm busy={voidBusy} onCancel={() => setVoiding(null)} onSubmit={(reason) => doVoid(s, r, reason)} />}
                    </div>
                  ))}
                </div>
              </details>
            )}
            <ResponseForm recipeId={recipeId} study={s} onDone={onReload} />
          </div>
        ))}
      </div>
    </Section>
  );
}
